import { useState } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '../Typography';
import Button from '../Button';
import CheckboxField from '../Field/CheckboxField';
import Modal from '../../componentsv2/Modal';
import * as service from '../../services/companyUsers';
import { useAppDispatch, loadCompanyUser, useAppSelector } from '../../store';
import { useTranslation } from '../../hooks/i18n';
import { filesURL } from '../../constants/auth';
import { homeEvents } from '../../constants/events';
import { Nullable } from '../../types';
import { COLORS } from '../../themev2';

interface AcknowledgementModalProps {
  isOpen: boolean;
}

const AcknowledgementModal = ({ isOpen }: AcknowledgementModalProps) => {
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);
  const [hasClickedLink, setHasClickedLink] = useState(false);

  const handleLinkClick = () => {
    setHasClickedLink(true);
    logEvent(homeEvents.CLICK_ACKNOWLEDGEMENT_LINK, {
      'Document URL': `${filesURL}/${t('common:acknowledgementModal.agreementDocument')}`,
    });
  };

  const handleAcceptanceChange = (checked: boolean) => {
    if (!hasClickedLink) {
      return;
    }
    setIsAccepted(checked);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!isAccepted || !selectedCompany?.id) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await service.confirmAcknowledgementAgreement(selectedCompany?.id);

      // Refresh company user data to update the flag
      await dispatch(loadCompanyUser()).unwrap();

      logEvent(homeEvents.CONFIRM_ACKNOWLEDGEMENT, {
        'Document URL': `${filesURL}/${t('common:acknowledgementModal.agreementDocument')}`,
      });
      setIsAccepted(false);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t('common:acknowledgementModal.errors.submissionFailed')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={() => {
        // Modal is non-dismissible, so this should not be called
      }}
      displayCloseButton={false}
      disableBackdropClick
      disableEscapeKeyDown
      size="medium"
      isAutoHeight
    >
      <Grid container rowSpacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
            {t('common:acknowledgementModal.title')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'left', color: COLORS.gray[1100] }}>
            {t('common:acknowledgementModal.description')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              border: `1px solid ${COLORS.gray[200]}`,
              borderRadius: '8px',
              padding: '1rem',
              backgroundColor: COLORS.gray[200],
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 400, color: COLORS.gray[1200] }}>
              {t('common:acknowledgementModal.agreementText')}
            </Typography>
            <Box sx={{ marginTop: '1rem' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 400, color: COLORS.gray[1200] }}>
                {t('common:acknowledgementModal.agreementFirstPart')}{' '}
                <a
                  href={`${filesURL}/${t('common:acknowledgementModal.agreementDocument')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.indigo[500], fontWeight: 600 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLinkClick();
                  }}
                >
                  {t('common:acknowledgementModal.agreementLink')}
                </a>
                .
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {!hasClickedLink && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: COLORS.gray[1100],
                fontStyle: 'italic',
                marginBottom: '0.5rem',
              }}
            >
              {t('common:acknowledgementModal.readAgreementHint')}
            </Typography>
          )}
          <CheckboxField
            label={t('common:acknowledgementModal.checkboxLabel')}
            value={isAccepted}
            onChange={handleAcceptanceChange}
            error={error || undefined}
            disabled={isLoading || !hasClickedLink}
          />
        </Grid>
        {error && (
          <Grid item xs={12}>
            <Typography sx={{ color: COLORS.feedback.danger, fontSize: '14px' }}>{error}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Button
            fullWidth
            color="secondary"
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={!isAccepted || isLoading}
          >
            {t('common:acknowledgementModal.confirmButton')}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default AcknowledgementModal;
